import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  CSSProperties,
  MouseEventHandler,
  TouchEventHandler,
  ReactNode,
} from 'react';
import { getParentPosition } from '../utility/dom-helpers';

interface ScrollElementProps {
  children: ReactNode;
  width: number;
  height: number;
  traditionalZoom: boolean;
  scrollRef: (e: HTMLDivElement) => void;
  onZoom: (n: number, m: number) => void;
  onWheelZoom: (speed: number, xPosition: number, deltaY: number) => void;
  onScroll: (n: number) => void;
}

export function ScrollElement({
  width,
  height,
  scrollRef,
  onZoom,
  onWheelZoom,
  onScroll,
  children,
}: ScrollElementProps) {
  const scrollComponentRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragLastPosition = useRef<number | null>(null);
  const lastTouchDistance = useRef<number | null>(null);
  const singleTouchStart = useRef<{ x: number; y: number; screenY: number } | null>(null);
  const lastSingleTouch = useRef<{ x: number; y: number; screenY: number } | null>(null);
  const isItemInteraction = useRef(false);

  const handleScroll = useCallback(() => {
    if (scrollComponentRef.current) {
      onScroll(scrollComponentRef.current.scrollLeft);
    }
  }, [onScroll]);

  // Refs para que el listener de wheel sea estable y siempre llame a la última onWheelZoom/onScroll.
  // Si el listener dependiera de handleWheel, al cambiar su identidad podríamos quitar el listener
  // correcto en el cleanup y dejar de recibir zoom.
  const onWheelZoomRef = useRef(onWheelZoom);
  const onScrollRef = useRef(onScroll);
  onWheelZoomRef.current = onWheelZoom;
  onScrollRef.current = onScroll;

  const handleWheelStable = useCallback((e: WheelEvent) => {
    const el = e.currentTarget as HTMLElement;
    if (e.ctrlKey || e.metaKey || e.altKey) {
      e.preventDefault();
      const parentPosition = getParentPosition(el);
      const xPosition = e.clientX - parentPosition.x;
      const speed = e.ctrlKey ? 10 : e.metaKey ? 3 : 1;
      onWheelZoomRef.current(speed, xPosition, e.deltaY);
    } else if (e.shiftKey) {
      e.preventDefault();
      if (scrollComponentRef.current) {
        onScrollRef.current(scrollComponentRef.current.scrollLeft + (e.deltaY || e.deltaX));
      }
    }
  }, []);

  const handleItemInteract = useCallback((e: Event) => {
    isItemInteraction.current = (
      e as CustomEvent<{ itemInteraction: boolean }>
    ).detail.itemInteraction;
  }, []);

  const refHandler = useCallback(
    (el: HTMLDivElement | null) => {
      if (scrollComponentRef.current) {
        scrollComponentRef.current.removeEventListener('wheel', handleWheelStable);
        scrollComponentRef.current.removeEventListener('itemInteraction', handleItemInteract);
      }
      scrollComponentRef.current = el;
      if (el) scrollRef(el);
      if (el) {
        el.addEventListener('wheel', handleWheelStable, { passive: false });
        el.addEventListener('itemInteraction', handleItemInteract);
      }
    },
    [scrollRef, handleWheelStable, handleItemInteract]
  );

  useEffect(
    () => () => {
      const el = scrollComponentRef.current;
      if (el) {
        el.removeEventListener('wheel', handleWheelStable);
        el.removeEventListener('itemInteraction', handleItemInteract);
      }
    },
    [handleWheelStable]
  );

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.button === 0) {
      dragLastPosition.current = e.pageX;
      setIsDragging(true);
    }
  }, []);

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (isDragging && !isItemInteraction.current && dragLastPosition.current !== null) {
        if (scrollComponentRef.current) {
          onScroll(scrollComponentRef.current.scrollLeft + dragLastPosition.current - e.pageX);
        }
        dragLastPosition.current = e.pageX;
      }
    },
    [isDragging, onScroll]
  );

  const handleMouseUp = useCallback(() => {
    dragLastPosition.current = null;
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    dragLastPosition.current = null;
    setIsDragging(false);
  }, []);

  const handleTouchStart: TouchEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      lastTouchDistance.current = Math.abs(e.touches[0].screenX - e.touches[1].screenX);
      singleTouchStart.current = null;
      lastSingleTouch.current = null;
    } else if (e.touches.length === 1) {
      e.preventDefault();
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      lastTouchDistance.current = null;
      singleTouchStart.current = { x, y, screenY: window.pageYOffset };
      lastSingleTouch.current = { x, y, screenY: window.pageYOffset };
    }
  }, []);

  const handleTouchMove: TouchEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (isItemInteraction.current) {
        e.preventDefault();
        return;
      }
      if (lastTouchDistance.current !== null && e.touches.length === 2) {
        e.preventDefault();
        const touchDistance = Math.abs(e.touches[0].screenX - e.touches[1].screenX);
        const parentPosition = getParentPosition(e.currentTarget);
        const xPosition = (e.touches[0].screenX + e.touches[1].screenX) / 2 - parentPosition.x;
        if (touchDistance !== 0 && lastTouchDistance.current !== 0) {
          onZoom(lastTouchDistance.current / touchDistance, xPosition / width);
          lastTouchDistance.current = touchDistance;
        }
      } else if (
        lastSingleTouch.current !== null &&
        singleTouchStart.current !== null &&
        e.touches.length === 1
      ) {
        e.preventDefault();
        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        const deltaX = x - lastSingleTouch.current.x;
        const deltaX0 = x - singleTouchStart.current.x;
        const deltaY0 = y - singleTouchStart.current.y;
        lastSingleTouch.current = { x, y, screenY: window.pageYOffset };
        const moveX = Math.abs(deltaX0) * 3 > Math.abs(deltaY0);
        const moveY = Math.abs(deltaY0) * 3 > Math.abs(deltaX0);
        if (deltaX !== 0 && moveX && scrollComponentRef.current) {
          onScroll(scrollComponentRef.current.scrollLeft - deltaX);
        }
        if (moveY) {
          window.scrollTo(window.pageXOffset, singleTouchStart.current.screenY - deltaY0);
        }
      }
    },
    [onZoom, onScroll, width]
  );

  const handleTouchEnd = useCallback(() => {
    lastTouchDistance.current = null;
    lastSingleTouch.current = null;
    singleTouchStart.current = null;
  }, []);

  const scrollComponentStyle: CSSProperties = {
    width: `${width}px`,
    height: `${height + 20}px`,
    cursor: isDragging ? 'move' : 'default',
    position: 'relative',
  };

  return (
    <div
      ref={refHandler}
      data-testid="scroll-element"
      className="rct-scroll"
      style={scrollComponentStyle}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onScroll={handleScroll}
    >
      {children}
    </div>
  );
}

ScrollElement.displayName = 'ScrollElement';

export default ScrollElement;
